<script setup lang="ts">
import { Trash2 as Trash2Icon } from '@lucide/vue'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { useIOSKeyboard } from '@/composables/useIOSKeyboard'
import { formatTime } from '@/services/formattingService'
import { requirePermission } from '@/services/permissionService'
import { shouldHideAscentForTrack } from '@/services/tourTypeService'
import { TrackColorService } from '@/services/trackColorService'
import {
  getTrackElevationGain,
  getTrackElevationLoss,
  getTrackElevationMax,
  getTrackElevationMin,
  getTrackTimeElapsed,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour, Track } from '@/types/tour'
import { optionalNameValidationRules } from '@/utils/validation'

const props = defineProps<{
  isOpen: boolean
  tourId: string
  trackIndex: number
}>()

const emit = defineEmits<{
  close: []
  save: [tourId: string, trackIndex: number, updates: { name: string; color: string }]
  'delete-track': [tourId: string, trackIndex: number]
}>()

const workspaceStore = useWorkspaceStore()
const { getInputHandlers } = useIOSKeyboard()

const trackColor = ref(TrackColorService.getDefaultColor())
const tour = ref<Tour | null>(null)
const track = ref<Track | null>(null)
const firstInput = ref<HTMLInputElement | null>(null)
const error = ref<string | null>(null)

// Permission checks are enforced at click via requirePermission

// Setup form validation
const { defineField, handleSubmit, errors, resetForm } = useForm({
  validationSchema: {
    trackName: optionalNameValidationRules,
  },
})

const [trackName, trackNameAttrs] = defineField('trackName')

const tourColors = TrackColorService.getColors()

const trackTimeElapsed = computed(() => (track.value ? getTrackTimeElapsed(track.value) : null))
const trackTimeMoving = computed(() => (track.value ? getTrackTimeMoving(track.value) : null))
const trackElevationGain = computed(() => {
  if (!track.value || shouldHideAscentForTrack(track.value, tour.value?.type)) return null
  return getTrackElevationGain(track.value)
})
const trackElevationLoss = computed(() => (track.value ? getTrackElevationLoss(track.value) : null))
const trackElevationMin = computed(() => (track.value ? getTrackElevationMin(track.value) : null))
const trackElevationMax = computed(() => (track.value ? getTrackElevationMax(track.value) : null))

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      loadTrackData()
    }
  },
  { immediate: true },
)

const loadTrackData = () => {
  tour.value = workspaceStore.getTourById(props.tourId) || null

  if (tour.value && props.trackIndex >= 0 && props.trackIndex < tour.value.tracks.length) {
    track.value = tour.value.tracks[props.trackIndex]

    // Use track color if defined, otherwise fall back to tour color
    const currentColor = track.value.color
    trackColor.value = TrackColorService.ensureValidColor(currentColor)

    resetForm({
      values: {
        trackName: track.value.name || '',
      },
    })
  }
}

const handleClose = () => {
  emit('close')
}

const saveTrack = handleSubmit(async (values) => {
  if (!tour.value || !track.value) return
  error.value = null

  try {
    emit('save', props.tourId, props.trackIndex, {
      name: values.trackName,
      color: trackColor.value,
    })
    handleClose()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'An unexpected error occurred.'
  }
})

const deleteTrack = async () => {
  if (!tour.value || !track.value) return
  // Guard with permission to show standardized dialog when denied
  if (!(await requirePermission('delete_track'))) return
  emit('delete-track', props.tourId, props.trackIndex)
}

const handleColorKeyDown = (e: KeyboardEvent, color: string) => {
  if (e.key === ' ' || e.key === 'Enter') {
    trackColor.value = color
    e.preventDefault()

    const liveRegion = document.getElementById('color-change-announcer')
    if (liveRegion) {
      const selectedColor = tourColors.find((c) => c.value === color)
      liveRegion.textContent = `Selected color: ${selectedColor?.label || color}`
    }
  }
}

const getTextColor = (bgColor: string): string => {
  return TrackColorService.getTextColor(bgColor)
}

const formatDistance = (meters: number): string => {
  return (meters / 1000).toFixed(1) + ' km'
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    title="Edit Track"
    description="Edit track details including name and color"
    size="lg"
    show-footer
    @close="handleClose"
  >
    <form @submit.prevent="saveTrack" class="space-y-4">
      <div>
        <label for="track-name" class="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          id="track-name"
          v-model="trackName"
          v-bind="trackNameAttrs"
          type="text"
          class="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
          :class="{ 'border-red-300': errors.trackName, 'border-gray-300': !errors.trackName }"
          placeholder="Track Name"
          ref="firstInput"
          :aria-invalid="errors.trackName ? 'true' : 'false'"
          :aria-describedby="errors.trackName ? 'track-name-error' : undefined"
          @focus="getInputHandlers().onFocus"
          @blur="getInputHandlers().onBlur"
        />
        <div
          v-if="errors.trackName"
          id="track-name-error"
          class="text-sm text-red-600 mt-1"
          role="alert"
        >
          {{ errors.trackName }}
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="error"
        class="p-3 bg-red-50 border border-red-200 rounded-md"
        role="alert"
        aria-live="polite"
      >
        <p class="text-sm text-red-600">{{ error }}</p>
      </div>

      <div>
        <label id="track-color-label" class="block text-sm font-medium text-gray-700 mb-2"
          >Color</label
        >
        <div
          class="grid grid-cols-5 gap-2"
          role="radiogroup"
          aria-labelledby="track-color-label"
          aria-required="true"
        >
          <button
            v-for="color in tourColors"
            :key="color.value"
            type="button"
            @click="trackColor = color.value"
            @keydown="(e: KeyboardEvent) => handleColorKeyDown(e, color.value)"
            :class="[
              'w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
              trackColor === color.value ? 'ring-2 ring-offset-2 ring-primary-500' : '',
            ]"
            :style="{ backgroundColor: color.value }"
            :title="color.label"
            :aria-label="color.label"
            role="radio"
            :aria-checked="trackColor === color.value"
            tabindex="0"
          >
            <span
              v-if="trackColor === color.value"
              class="text-xs font-bold"
              :style="{ color: getTextColor(color.value) }"
              >✓</span
            >
          </button>
        </div>
        <div id="color-change-announcer" class="sr-only" aria-live="polite"></div>
      </div>

      <div v-if="track" class="mt-4 p-3 bg-gray-50 rounded-md border border-gray-100">
        <div class="text-sm font-medium text-gray-700 mb-2">Track Statistics</div>
        <div class="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span class="text-xs text-gray-500">Tour:</span>
            <span class="ml-1 font-medium">{{ tour?.name || 'Unknown' }}</span>
          </div>
          <div>
            <span class="text-xs text-gray-500">Points:</span>
            <span class="ml-1 font-medium">{{
              track.segments.flatMap((segment) => segment.points).length
            }}</span>
          </div>
          <!-- Distance -->
          <div v-if="track.metrics?.distance">
            <span class="text-xs text-gray-500">Distance:</span>
            <span class="ml-1 font-medium">{{ formatDistance(track.metrics.distance) }}</span>
          </div>
          <!-- Time elapsed -->
          <div v-if="trackTimeElapsed !== null">
            <span class="text-xs text-gray-500">Duration:</span>
            <span class="ml-1 font-medium">{{ formatTime(trackTimeElapsed) }}</span>
          </div>
          <!-- Time moving -->
          <div v-if="trackTimeMoving !== null && trackTimeMoving !== trackTimeElapsed">
            <span class="text-xs text-gray-500">Moving time:</span>
            <span class="ml-1 font-medium">{{ formatTime(trackTimeMoving) }}</span>
          </div>
          <!-- Elevation gain -->
          <div v-if="trackElevationGain !== null">
            <span class="text-xs text-gray-500">Elevation gain:</span>
            <span class="ml-1 font-medium">{{ trackElevationGain }}m</span>
          </div>
          <!-- Elevation loss -->
          <div v-if="trackElevationLoss !== null">
            <span class="text-xs text-gray-500">Elevation loss:</span>
            <span class="ml-1 font-medium">{{ trackElevationLoss }}m</span>
          </div>
          <!-- Min/Max elevation -->
          <div v-if="trackElevationMin !== null">
            <span class="text-xs text-gray-500">Min elevation:</span>
            <span class="ml-1 font-medium">{{ trackElevationMin }}m</span>
          </div>
          <div v-if="trackElevationMax !== null">
            <span class="text-xs text-gray-500">Max elevation:</span>
            <span class="ml-1 font-medium">{{ trackElevationMax }}m</span>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex flex-col gap-3 w-full">
        <!-- Primary actions -->
        <div class="flex gap-3">
          <BaseButton @click="handleClose" variant="secondary"> Cancel </BaseButton>
          <BaseButton @click="saveTrack" variant="primary"> Save Changes </BaseButton>
        </div>

        <!-- Destructive action -->
        <BaseButton @click="deleteTrack" variant="danger" full-width aria-label="Delete track">
          <Trash2Icon class="w-4 h-4" />
          <span>Delete Track</span>
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>
